import React, { useMemo } from 'react';
import { Box, Typography, Paper, Grid, Card, CardContent } from '@mui/material';
import { Business, Person, TrendingUp, Assessment } from '@mui/icons-material';

interface ColumnChartDashboardProps {
  categoryScores: Record<string, number>;
  language: string;
}

const categoryNames: Record<string, { hebrew: string; english: string }> = {
  'management': { hebrew: 'ניהול', english: 'Management' },
  'organization': { hebrew: 'ארגון', english: 'Organization' },
  'repeatSales': { hebrew: 'מכירות חוזרות', english: 'Repeat Sales' },
  'finance': { hebrew: 'כספים', english: 'Finance' },
  'operations': { hebrew: 'תפעול', english: 'Operations' },
  'qualityControl': { hebrew: 'בקרת איכות', english: 'Quality Control' },
  'newSales': { hebrew: 'מכירות חדשות', english: 'New Sales' },
  'stress': { hebrew: 'מתח', english: 'Stress' },
  'principles': { hebrew: 'עקרונות', english: 'Principles' },
  'helpAcceptance': { hebrew: 'קבלת עזרה', english: 'Help' },
};

const ColumnChartDashboard: React.FC<ColumnChartDashboardProps> = ({ categoryScores, language }) => {
  const processedScores = useMemo(() => {
    const scores = Object.entries(categoryScores).map(([_, score], index) => {
      const categoryKeys = Object.keys(categoryNames);
      const categoryKey = categoryKeys[index] || 'management';
      return {
        key: categoryKey,
        name: language === 'he' ? categoryNames[categoryKey]?.hebrew : categoryNames[categoryKey]?.english,
        value: score as number
      };
    });

    return {
      business: scores.slice(0, 7),
      personal: scores.slice(7, 10),
      businessAverage: scores.slice(0, 7).reduce((sum, cat) => sum + cat.value, 0) / 7,
      personalAverage: scores.slice(7, 10).reduce((sum, cat) => sum + cat.value, 0) / 3,
    };
  }, [categoryScores, language]);

  const getColumnColor = (score: number) => {
    if (score >= 80) return { start: '#27ae60', end: '#2ecc71' };
    if (score >= 60) return { start: '#2ecc71', end: '#52c77c' };
    if (score >= 40) return { start: '#f39c12', end: '#f1c40f' };
    return { start: '#e74c3c', end: '#e67e22' };
  };

  const renderColumn = (category: any, maxHeight: number = 250) => {
    const height = (category.value / 100) * maxHeight;
    const colors = getColumnColor(category.value);

    return (
      <Box
        key={category.key}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flex: 1,
          minWidth: '70px'
        }}
      >
        {/* Value label on top */}
        <Typography
          variant="body2"
          fontWeight="bold"
          sx={{
            mb: 0.5,
            color: colors.start,
            fontSize: '0.95rem'
          }}
        >
          {category.value.toFixed(0)}%
        </Typography>

        {/* Column container */}
        <Box
          sx={{
            height: `${maxHeight}px`,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            position: 'relative'
          }}
        >
          {/* Grid lines */}
          {[100, 80, 60, 40, 20].map((line) => (
            <Box
              key={line}
              sx={{
                position: 'absolute',
                bottom: `${(line / 100) * maxHeight}px`,
                left: -4,
                right: -4,
                height: '1px',
                backgroundColor: '#e0e0e0',
                zIndex: 0
              }}
            />
          ))}

          {/* Animated column */}
          <Box
            sx={{
              height: `${height}px`,
              background: `linear-gradient(180deg, ${colors.end} 0%, ${colors.start} 100%)`,
              borderRadius: '8px 8px 0 0',
              position: 'relative',
              transition: 'height 0.8s ease-out',
              boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
              zIndex: 1,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'rgba(255,255,255,0.4)',
                borderRadius: '8px 8px 0 0'
              }
            }}
          />
        </Box>

        {/* Category name */}
        <Typography
          variant="caption"
          sx={{
            mt: 1,
            textAlign: 'center',
            fontWeight: 600,
            fontSize: '0.7rem',
            lineHeight: 1.2,
            maxWidth: '80px'
          }}
        >
          {category.name}
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3, textAlign: 'center' }}>
        {language === 'he' ? '📊 תרשים עמודות אנכי' : '📊 Vertical Column Chart'}
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Business sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {language === 'he' ? 'ממוצע עסקי' : 'Business Average'}
              </Typography>
              <Typography variant="h3" fontWeight="bold" sx={{ mt: 1 }}>
                {processedScores.businessAverage.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Person sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {language === 'he' ? 'ממוצע אישי' : 'Personal Average'}
              </Typography>
              <Typography variant="h3" fontWeight="bold" sx={{ mt: 1 }}>
                {processedScores.personalAverage.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Assessment sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {language === 'he' ? 'ממוצע כללי' : 'Overall Average'}
              </Typography>
              <Typography variant="h3" fontWeight="bold" sx={{ mt: 1 }}>
                {((processedScores.businessAverage * 7 + processedScores.personalAverage * 3) / 10).toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Business Columns */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Business sx={{ color: '#667eea' }} />
          <Typography variant="h6" fontWeight="bold">
            {language === 'he' ? 'קטגוריות עסקיות' : 'Business Categories'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-around', position: 'relative' }}>
          {/* Y-axis labels */}
          <Box sx={{ position: 'absolute', left: -35, top: 30, bottom: 45, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            {[100, 80, 60, 40, 20, 0].map(val => (
              <Typography key={val} variant="caption" sx={{ fontSize: '0.7rem', color: '#666' }}>
                {val}%
              </Typography>
            ))}
          </Box>
          {processedScores.business.map(cat => renderColumn(cat, 250))}
        </Box>
      </Paper>

      {/* Personal Columns */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Person sx={{ color: '#f5576c' }} />
          <Typography variant="h6" fontWeight="bold">
            {language === 'he' ? 'קטגוריות אישיות' : 'Personal Categories'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-around', position: 'relative' }}>
          {/* Y-axis labels */}
          <Box sx={{ position: 'absolute', left: -35, top: 30, bottom: 45, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            {[100, 80, 60, 40, 20, 0].map(val => (
              <Typography key={val} variant="caption" sx={{ fontSize: '0.7rem', color: '#666' }}>
                {val}%
              </Typography>
            ))}
          </Box>
          {processedScores.personal.map(cat => renderColumn(cat, 250))}
        </Box>
      </Paper>

      {/* Performance Indicator */}
      <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <TrendingUp sx={{ fontSize: 32, color: '#667eea' }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight="600">
            {language === 'he' ? 'ניתוח מגמה' : 'Trend Analysis'}
          </Typography>
          <Typography variant="caption" sx={{ color: '#666' }}>
            {processedScores.business.filter(c => c.value >= 60).length} {language === 'he' ? 'מתוך' : 'out of'} 7 {language === 'he' ? 'קטגוריות עסקיות מעל 60%' : 'business categories above 60%'}
            {' • '}
            {processedScores.personal.filter(c => c.value >= 60).length} {language === 'he' ? 'מתוך' : 'out of'} 3 {language === 'he' ? 'קטגוריות אישיות מעל 60%' : 'personal categories above 60%'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ColumnChartDashboard;
